import type { FieldResult, OcrBox } from '../shared/desktop-api';

/**
 * The marked box a checkbox reading chose (the engine's ChoiceFieldStep, docs/planning/
 * choice-fields-checkbox-reading.md), when the field still holds the value that box chose.
 * It is drawn beside the caption's lines so the person sees which box was read, not only
 * which words. Geometry that is not a real area is no evidence.
 */
export function markEvidenceOf(
  field: Pick<FieldResult, 'markEvidence'> | null | undefined,
): OcrBox | null {
  const box = field?.markEvidence;
  return box &&
    [box.x, box.y, box.width, box.height].every(Number.isFinite) &&
    box.x >= 0 &&
    box.y >= 0 &&
    box.width > 0 &&
    box.height > 0
    ? box
    : null;
}

/**
 * The marked boxes to draw while a page shows these evidence lines: a checkbox field's box is
 * its caption's companion, so it is shown whenever exactly the caption lines it cites are the
 * evidence on show (a field clicked, a hover, a pin) on that field's page.
 */
export function markEvidenceForLines(
  fields: readonly FieldResult[],
  pageNumber: number | undefined,
  defaultPageNumber: number | undefined,
  lines: readonly number[],
): OcrBox[] {
  if (pageNumber == null || lines.length === 0) return [];
  const shown = new Set(lines);
  return fields
    .flatMap((field) => {
      const box = markEvidenceOf(field);
      const cited = field.evidenceLineIndexes ?? [];
      return box &&
        (field.evidencePageNumber ?? defaultPageNumber) === pageNumber &&
        cited.length > 0 &&
        cited.every((line) => shown.has(line))
        ? [box]
        : [];
    })
    .filter(
      (box, index, boxes) =>
        boxes.findIndex((other) => other.x === box.x && other.y === box.y) === index,
    );
}
